import { DataTableFacetedForm } from '@/components/commons/data-table/data-table-faceted-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/use-toast';
import { QueryParams } from '@/hooks/use-pagination';
import { Cross2Icon } from '@radix-ui/react-icons';
import { format } from 'date-fns';
import { Search } from 'lucide-react';
import React from 'react';

type FilterCustomerOrder = {
  tab: string;
  params: QueryParams;
  setParams: (params: QueryParams) => void;
};

export default function FilterCustomerOrder({ params, setParams }: FilterCustomerOrder) {
  const dateRangeOptions = React.useMemo(
    () => [
      {
        label: 'Start Date',
        value: 'start_date',
        valValue: params?.startDate || '',
        mode: 'date' as const,
      },
      {
        label: 'End Date',
        value: 'end_date',
        valValue: params?.endDate || '',
        mode: 'date' as const,
      },
    ],
    [params]
  );

  const handleFilterDateRange = React.useCallback(
    (values: Array<{ value: string; valValue: string }> | undefined) => {
      if (!values) return;
      const filteredQuery = values.reduce<{ startDate?: string; endDate?: string }>((acc, item) => {
        if (item.value === 'start_date' && item.valValue) {
          const startDate = new Date(item.valValue);
          startDate.setHours(0, 0, 0, 0);
          acc.startDate = format(startDate, 'yyyy-MM-dd HH:mm:ss');
        } else if (item.value === 'end_date' && item.valValue) {
          const endDate = new Date(item.valValue);
          endDate.setHours(23, 59, 59, 999);
          acc.endDate = format(endDate, 'yyyy-MM-dd HH:mm:ss');
        }
        return acc;
      }, {});

      if (!filteredQuery.startDate) {
        toast({
          title: 'Error',
          description: 'Please select the start date!',
          variant: 'destructive',
        });
        return;
      }

      setParams({
        ...params,
        pageIndex: 1,
        startDate: filteredQuery.startDate,
        endDate: filteredQuery.endDate || '',
      });
    },
    [setParams, params]
  );

  const handleClearAllFilters = React.useCallback(() => {
    setParams({
      status: '',
      q: '',
      startDate: '',
      endDate: '',
      pageIndex: 1,
      pageSize: 10,
    });
  }, [setParams]);

  const handleSearch = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setParams({
        ...params,
        q: e.target.value,
      });
    },
    [setParams]
  );

  return (
    <div className='mb-3 w-full'>
      <div className='flex flex-wrap items-center justify-between space-y-2 sm:space-x-2 sm:space-y-0'>
        <div className='flex flex-1 flex-col items-start gap-2 md:flex-row md:items-center'>
          <Input
            name='keyword'
            type='text'
            placeholder='Search'
            leftContent={<Search className='mx-auto w-4' />}
            classNameWrapper='w-full sm:w-auto'
            value={params.q || ''}
            onChange={handleSearch}
            data-testid='search-order-customer'
          />

          <div>
            <DataTableFacetedForm
              title='Date Range'
              options={dateRangeOptions}
              onApply={handleFilterDateRange}
            />
          </div>

          <Button
            variant='ghost'
            onClick={handleClearAllFilters}
            className='p-4'
            data-testid='clear-filter-order-customer'
          >
            Clear all
            <Cross2Icon className='ml-2 h-4 w-4' />
          </Button>
        </div>
      </div>
    </div>
  );
}
